import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormControl, FormHelperText, TextField } from '@mui/material';
import { isNil } from 'lodash';

import { useFocusConditionTemplatesQuery } from '@common/cache';
import { SearchField } from 'components/SearchField';
import { OTHER_SPECIFY_VALUE } from '../constants';

const getOptionLabel = (condition: string, icd10Codes: string[]) =>
  icd10Codes.length ? `${condition} (${icd10Codes.join(', ')})` : condition;

interface IFocusConditionFormFieldProps {
  name: string;
  actionItemsName: string;
  otherConditionTextName: string;
  placeholder: string;
  disabled?: boolean;
}

export const FocusConditionFormField = ({
  name,
  actionItemsName,
  otherConditionTextName,
  placeholder,
  disabled = false,
}: IFocusConditionFormFieldProps) => {
  const { control, setValue } = useFormContext();
  const { data: templates = [] } = useFocusConditionTemplatesQuery();

  const [search, setSearch] = useState<string | undefined>(undefined);

  return (
    <Controller
      name={name}
      control={control}
      disabled={disabled}
      render={({ field, fieldState: { error } }) => {
        const isOtherSpecify = field.value === OTHER_SPECIFY_VALUE;

        const query = search?.toLowerCase();

        const otherSpecifyOption =
          !query || OTHER_SPECIFY_VALUE.toLowerCase().includes(query)
            ? [{ id: OTHER_SPECIFY_VALUE, name: OTHER_SPECIFY_VALUE }]
            : [];

        const templateOptions = templates
          .filter(template => {
            if (!query) return true;

            return (
              template.condition.toLowerCase().includes(query) ||
              template.icd10_codes.some(code =>
                code.toLowerCase().includes(query),
              )
            );
          })
          .map(template => ({
            id: template.condition,
            name: getOptionLabel(template.condition, template.icd10_codes),
          }));

        // Conditions saved as free text before this field became a select are
        // not part of the template list, so keep them selectable.
        const isExistingFreeText =
          field.value &&
          field.value !== OTHER_SPECIFY_VALUE &&
          !templates.some(({ condition }) => condition === field.value);

        const mergedOptions = [
          ...otherSpecifyOption,
          ...(isExistingFreeText
            ? [{ id: field.value, name: field.value }]
            : []),
          ...templateOptions,
        ];

        return (
          <FormControl fullWidth>
            <SearchField
              name={name}
              placeholder={placeholder}
              options={mergedOptions}
              onSearchChange={newSearch => setSearch(newSearch || undefined)}
              onValueChange={newValue => {
                if (disabled) return;

                field.onChange(newValue ?? '');
                setSearch(undefined);

                if (newValue === OTHER_SPECIFY_VALUE) {
                  setValue(actionItemsName, '', {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  setValue(otherConditionTextName, '', {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  return;
                }

                const selected = templates.find(
                  ({ condition }) => condition === newValue,
                );
                setValue(actionItemsName, selected?.action_items ?? '', {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                setValue(otherConditionTextName, '', {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
              value={field.value ?? ''}
              search={search}
              disabled={disabled}
              optional
              error={!isNil(error)}
            />
            <FormHelperText error={!isNil(error)}>
              {error
                ? error.message
                : 'Search by entering ICD-10 code (e.g., E11.9) or the name of the condition (e.g., Type 2 Diabetes) to select from the list.'}
            </FormHelperText>
            {isOtherSpecify && (
              <Controller
                name={otherConditionTextName}
                control={control}
                render={({
                  field: otherField,
                  fieldState: { error: otherError },
                }) => (
                  <TextField
                    {...otherField}
                    sx={{ mt: 2, mb: 0.5 }}
                    label='Specify condition'
                    multiline
                    minRows={2}
                    maxRows={2}
                    fullWidth
                    required
                    disabled={disabled}
                    error={!isNil(otherError)}
                    helperText={otherError ? otherError.message : undefined}
                    slotProps={{
                      formHelperText: {
                        style: {
                          textAlign: otherError ? 'left' : 'right',
                          margin: 0,
                          padding: '3px 14px 0',
                        },
                      },
                    }}
                  />
                )}
              />
            )}
          </FormControl>
        );
      }}
    />
  );
};
